import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, ArrowLeft, Mail } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/auth";

/**
 * ForgotPassword — étape 1 du reset : l'artisan saisit son email pour recevoir
 * un lien de réinitialisation. Réponse serveur toujours identique (200) — on
 * ne divulgue jamais si l'email existe (anti-énumération).
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <main data-testid="forgot-success" className="flex justify-center px-6 pt-12 sm:pt-20 pb-16">
        <div className="paper p-10 max-w-md text-center space-y-4">
          <Mail className="w-12 h-12 mx-auto text-primary" strokeWidth={1.4} />
          <h1 className="font-heading text-3xl">Vérifiez votre boîte mail</h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Si un compte existe pour <b>{email}</b>, un lien de réinitialisation vient d&apos;être envoyé.
            Le lien reste valable <b>30 minutes</b>.
          </p>
          <p className="text-xs text-muted-foreground bg-secondary/50 rounded-lg p-4">
            Vous ne recevez rien ? Vérifiez le dossier « Indésirables » ou re-demandez un lien.
          </p>
          <Link
            to="/login"
            data-testid="forgot-back-login"
            className="btn-secondary w-full inline-flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" /> Retour à la connexion
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="forgot-page" className="flex justify-center px-6 pt-12 sm:pt-20 pb-16">
      <form onSubmit={submit} className="paper p-8 lg:p-10 w-full max-w-md space-y-5">
        <div>
          <p className="eyebrow mb-2">Espace professionnel</p>
          <h1 className="text-3xl">Mot de passe oublié</h1>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            Renseignez votre email professionnel. Nous vous envoyons un lien sécurisé pour choisir un nouveau mot de passe.
          </p>
        </div>
        <div>
          <Label>Email</Label>
          <Input
            data-testid="forgot-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="vous@entreprise.fr"
          />
        </div>
        <Button data-testid="forgot-submit" type="submit" className="btn-primary w-full" disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Envoyer le lien <CheckCircle2 className="w-4 h-4" /></>}
        </Button>
        <p className="text-sm text-muted-foreground text-center">
          <Link to="/login" data-testid="forgot-back-link" className="text-primary font-medium hover:underline inline-flex items-center gap-1">
            <ArrowLeft className="w-3.5 h-3.5" /> Retour à la connexion
          </Link>
        </p>
      </form>
    </main>
  );
}
